import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MessageCircle } from "lucide-react";
import Monogram from "@/components/Monogram";
import { scrollToSection, WHATSAPP_URL } from "@/lib/scroll";

const LINKS = [
  { label: "Work", href: "#work", id: "nav-link-work" },
  { label: "Services", href: "#services", id: "nav-link-services" },
  { label: "Clients", href: "#clients", id: "nav-link-clients" },
  { label: "Contact", href: "#contact", id: "nav-link-contact" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    scrollToSection(href);
  };

  return (
    <>
      <header
        data-testid="navbar-header"
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled ? "bg-[#070709]/75 backdrop-blur-xl border-b border-white/5" : "bg-transparent"
        }`}
      >
        <div className="wrap flex h-[72px] items-center justify-between">
          <a
            href="#top"
            data-testid="navbar-brand-logo"
            onClick={(e) => {
              e.preventDefault();
              if (window.__lenis) window.__lenis.scrollTo(0);
              else window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            className="flex items-center gap-3"
          >
            <Monogram className="h-9 w-9" />
            <span className="font-display text-lg font-bold uppercase tracking-[0.18em]">
              Ayan Jafri
            </span>
          </a>

          <nav className="hidden items-center gap-9 md:flex">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={l.id}
                onClick={(e) => go(e, l.href)}
                className="mono-label text-[11px] text-zinc-400 transition-colors duration-300 hover:text-white"
              >
                {l.label}
              </a>
            ))}
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-cta-whatsapp-button"
              className="btn-primary !py-2.5 !px-5 text-sm"
            >
              <MessageCircle className="h-4 w-4" />
              Start a project
            </a>
          </nav>

          <button
            className="md:hidden text-white p-2"
            data-testid="nav-mobile-toggle-button"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
          >
            <Menu className="h-6 w-6" />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            data-testid="nav-mobile-menu"
            className="fixed inset-0 z-[90] flex flex-col bg-[#070709]/97 backdrop-blur-2xl md:hidden"
          >
            <div className="wrap flex h-[72px] items-center justify-between">
              <Monogram className="h-9 w-9" />
              <button
                className="p-2 text-white"
                data-testid="nav-mobile-close-button"
                onClick={() => setOpen(false)}
                aria-label="Close menu"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-2 px-8">
              {LINKS.map((l, i) => (
                <motion.a
                  key={l.href}
                  href={l.href}
                  data-testid={`${l.id}-mobile`}
                  onClick={(e) => go(e, l.href)}
                  initial={{ y: 40, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.08 * i, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  className="border-b border-white/5 py-4 font-display text-5xl font-black uppercase text-white"
                >
                  {l.label}
                </motion.a>
              ))}
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-mobile-cta-whatsapp-button"
                className="btn-primary mt-8 self-start"
              >
                <MessageCircle className="h-5 w-5" />
                Start a project
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
